import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Copy, Users, Gift, TrendingUp, Calendar, Share2, Mail } from 'lucide-react';

export default function CreditsPage() {
  const { token } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(0);
  const [referralStats, setReferralStats] = useState(null);
  const [creditHistory, setCreditHistory] = useState([]);
  const [referralCodeInput, setReferralCodeInput] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [hasReferrer, setHasReferrer] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Fetch credits balance
      const balanceRes = await axios.get(`${API}/credits/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCredits(balanceRes.data.credits || 0);

      // Fetch referral stats
      const statsRes = await axios.get(`${API}/credits/referral-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralStats(statsRes.data);
      
      // Check if user already has a referrer (to show/hide the apply code section)
      setHasReferrer(!!statsRes.data.referred_by);

      // Fetch credit history
      const historyRes = await axios.get(`${API}/credits/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreditHistory(historyRes.data.history || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load credits data');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = async () => {
    if (!referralStats?.referral_code) {
      toast.error('No referral code available');
      return;
    }

    const textToCopy = referralStats.referral_code;
    
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
        toast.success('Referral code copied to clipboard!');
        return;
      }
    } catch (clipboardErr) {
      console.warn('Clipboard API failed, trying fallback:', clipboardErr);
    }

    // Fallback method using textarea
    try {
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '0';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success('Referral code copied to clipboard!');
      } else {
        toast.error('Failed to copy code. Please copy manually.');
      }
    } catch (fallbackErr) {
      console.error('Fallback copy failed:', fallbackErr);
      toast.error('Failed to copy code. Please copy manually.');
    }
  };

  const copyReferralLink = async () => {
    if (!referralStats?.referral_code) {
      toast.error('No referral code available');
      return;
    }

    const referralLink = `${window.location.origin}/?ref=${referralStats.referral_code}`;
    
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(referralLink);
        toast.success('Referral link copied to clipboard!');
        return;
      }
    } catch (clipboardErr) {
      console.warn('Clipboard API failed, trying fallback:', clipboardErr);
    }

    // Fallback method using textarea
    try {
      const textArea = document.createElement('textarea');
      textArea.value = referralLink;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '0';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success('Referral link copied to clipboard!');
      } else {
        toast.error('Failed to copy link. Please copy manually.');
      }
    } catch (fallbackErr) {
      console.error('Fallback copy failed:', fallbackErr);
      toast.error('Failed to copy link. Please copy manually.');
    }
  };

  const shareToWhatsApp = () => {
    if (!referralStats?.referral_code) {
      toast.error('No referral code available');
      return;
    }
    const referralLink = `${window.location.origin}/?ref=${referralStats.referral_code}`;
    const message = `🚀 Join RefrX and build professional CVs with AI!\n\nUse my referral code: ${referralStats.referral_code}\n\n✅ Get 25 FREE credits to start\n✅ AI-powered CV & Cover Letter generator\n\nRegister here: ${referralLink}`;
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const shareToTwitter = () => {
    if (!referralStats?.referral_code) {
      toast.error('No referral code available');
      return;
    }
    const referralLink = `${window.location.origin}/?ref=${referralStats.referral_code}`;
    const message = `Join RefrX using my referral code ${referralStats.referral_code} and get 25 free credits! 🚀`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(referralLink)}`;
    window.open(twitterUrl, '_blank', 'noopener,noreferrer');
  };

  const shareToFacebook = () => {
    if (!referralStats?.referral_code) {
      toast.error('No referral code available');
      return;
    }
    const referralLink = `${window.location.origin}/?ref=${referralStats.referral_code}`;
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}&quote=${encodeURIComponent('Join RefrX and get 25 free credits to build professional CVs!')}`;
    window.open(facebookUrl, '_blank', 'noopener,noreferrer');
  };

  const shareToLinkedIn = () => {
    if (!referralStats?.referral_code) {
      toast.error('No referral code available');
      return;
    }
    const referralLink = `${window.location.origin}/?ref=${referralStats.referral_code}`;
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`;
    window.open(linkedinUrl, '_blank', 'noopener,noreferrer');
  };

  const shareToInstagram = () => {
    if (!referralStats?.referral_code) {
      toast.error('No referral code available');
      return;
    }
    // Instagram doesn't support direct sharing via URL, so we'll copy the message to clipboard
    const referralLink = `${window.location.origin}/?ref=${referralStats.referral_code}`;
    const message = `🚀 Join RefrX and build professional CVs with AI!\n\nUse my referral code: ${referralStats.referral_code}\n✅ Get 25 FREE credits\n\nRegister: ${referralLink}`;
    
    // Copy to clipboard
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(message).then(() => {
        toast.success('Message copied! You can now paste it on Instagram');
      }).catch(() => {
        toast.error('Failed to copy message');
      });
    } else {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = message;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Message copied! You can now paste it on Instagram');
      } catch (err) {
        toast.error('Failed to copy message');
      }
      document.body.removeChild(textArea);
    }
  };

  const shareViaEmail = () => {
    const referralLink = `${window.location.origin}/?ref=${referralStats?.referral_code}`;
    const subject = 'Join RefrX with my referral code!';
    const body = `Hi,\n\nI'm using RefrX to create professional CVs and thought you might find it useful!\n\nUse my referral code: ${referralStats?.referral_code}\nOr click this link to register: ${referralLink}\n\nYou'll get 25 free credits to start!\n\nBest regards`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  const applyReferralCode = async () => {
    if (!referralCodeInput.trim()) {
      toast.error('Please enter a referral code');
      return;
    }

    setApplyingCode(true);
    try {
      const response = await axios.post(
        `${API}/credits/apply-referral-code`,
        { referral_code: referralCodeInput.trim().toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      setHasReferrer(true);
      setReferralCodeInput('');
      
      // Refresh data to show updated credits
      await fetchAllData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to apply referral code';
      toast.error(errorMsg);
    } finally {
      setApplyingCode(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8">
      <div className="container mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-2">Credits & Referrals</h1>
            <p className="text-gray-600">Earn and track your credits</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Credits Balance Card */}
              <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                <CardContent className="p-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-indigo-100 text-sm mb-1">Your Balance</p>
                      <h2 className="text-5xl font-bold">{credits}</h2>
                      <p className="text-indigo-100 text-lg mt-1">Credits</p>
                      <Button
                        onClick={() => navigate('/purchase-credits')}
                        className="mt-4 bg-white text-indigo-600 hover:bg-indigo-50"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Buy More Credits
                      </Button>
                    </div>
                    <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-between">
                      <Gift className="w-10 h-10" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Referral Stats Grid */}
              <div className="grid md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Total Referrals</CardTitle>
                    <Users className="w-4 h-4 text-indigo-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{referralStats?.total_referrals || 0}</div>
                    <p className="text-xs text-gray-500 mt-1">People you've referred</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Bonuses Earned</CardTitle>
                    <TrendingUp className="w-4 h-4 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{referralStats?.bonuses_earned || 0}</div>
                    <p className="text-xs text-gray-500 mt-1">Milestone bonuses received</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Next Milestone</CardTitle>
                    <Gift className="w-4 h-4 text-purple-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{referralStats?.progress_to_next_bonus || 0}/{referralStats?.next_milestone ? referralStats.next_milestone - ((referralStats.bonuses_earned || 0) * 5) : 5}</div>
                    <p className="text-xs text-gray-500 mt-1">
                      Next bonus at {referralStats?.next_milestone || 5} referrals (25 credits)
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Apply Referral Code - One Time Only (if not already applied) */}
              {!hasReferrer && (
                <Card className="border-2 border-green-200 bg-green-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-green-900">
                      <Gift className="w-5 h-5" />
                      Have a Referral Code?
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-green-200">
                      <p className="text-sm text-green-900 mb-3">
                        If someone referred you to RefrX, enter their referral code below to earn <strong>10 bonus credits!</strong>
                      </p>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={referralCodeInput}
                          onChange={(e) => setReferralCodeInput(e.target.value.toUpperCase())}
                          placeholder="Enter referral code (e.g., ABC12345)"
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 uppercase"
                          maxLength={8}
                          disabled={applyingCode}
                        />
                        <Button
                          onClick={applyReferralCode}
                          disabled={applyingCode || !referralCodeInput.trim()}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          {applyingCode ? 'Applying...' : 'Apply Code'}
                        </Button>
                      </div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-xs text-yellow-900">
                        <strong>Note:</strong> Referral codes can only be applied <strong>once per account</strong>. Make sure to enter the correct code!
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Referral Code Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Your Referral Code</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-100 rounded-lg p-4 font-mono text-2xl font-bold text-center">
                      {referralStats?.referral_code || 'Loading...'}
                    </div>
                    <Button onClick={copyReferralCode} className="bg-indigo-600 hover:bg-indigo-700">
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </Button>
                  </div>
                  
                  {/* Referral Link with Copy */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Referral Link</label>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono text-gray-700 truncate">
                        {`${window.location.origin}/?ref=${referralStats?.referral_code || ''}`}
                      </div>
                      <Button 
                        onClick={copyReferralLink} 
                        variant="outline"
                        className="shrink-0"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Link
                      </Button>
                    </div>
                  </div>

                  {/* Social Sharing Buttons */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Share2 className="w-4 h-4 text-gray-600" />
                      <label className="text-sm font-medium text-gray-700">Share on Social Media</label>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                      {/* WhatsApp */}
                      <Button
                        onClick={shareToWhatsApp}
                        className="bg-[#25D366] hover:bg-[#20BD5A] text-white"
                        size="sm"
                      >
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        WhatsApp
                      </Button>

                      {/* Instagram */}
                      <Button
                        onClick={shareToInstagram}
                        className="bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] hover:opacity-90 text-white"
                        size="sm"
                      >
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
                        </svg>
                        Instagram
                      </Button>

                      {/* LinkedIn */}
                      <Button
                        onClick={shareToLinkedIn}
                        className="bg-[#0077B5] hover:bg-[#006399] text-white"
                        size="sm"
                      >
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                        LinkedIn
                      </Button>

                      {/* Twitter/X */}
                      <Button
                        onClick={shareToTwitter}
                        className="bg-black hover:bg-gray-800 text-white"
                        size="sm"
                      >
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                        X (Twitter)
                      </Button>

                      {/* Facebook */}
                      <Button
                        onClick={shareToFacebook}
                        className="bg-[#1877F2] hover:bg-[#166FE5] text-white"
                        size="sm"
                      >
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                        Facebook
                      </Button>

                      {/* Email */}
                      <Button
                        onClick={shareViaEmail}
                        className="bg-gray-600 hover:bg-gray-700 text-white"
                        size="sm"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Email
                      </Button>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900">
                      <strong>How it works:</strong> Share your referral code with friends! You'll earn <strong>25 bonus credits</strong> for every 5 people who register using your code.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Referral Milestones Progress */}
              <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-purple-900">
                    <TrendingUp className="w-5 h-5" />
                    Referral Milestones
                  </CardTitle>
                  <p className="text-sm text-gray-600 mt-2">
                    Earn 25 bonus credits for every 5 successful referrals!
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Milestone Progress Bar */}
                    {[5, 10, 15, 20, 25, 30].map((milestone) => {
                      const totalReferrals = (referralStats?.bonuses_earned || 0) * 5 + (referralStats?.progress_to_next_bonus || 0);
                      const isCompleted = totalReferrals >= milestone;
                      const isCurrent = !isCompleted && totalReferrals < milestone && (milestone <= (referralStats?.next_milestone || 5));
                      const isNext = milestone === (referralStats?.next_milestone || 5);
                      
                      return (
                        <div key={milestone} className="flex items-center gap-4">
                          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                            isCompleted 
                              ? 'bg-green-500 text-white' 
                              : isNext 
                                ? 'bg-purple-500 text-white ring-4 ring-purple-200' 
                                : 'bg-gray-200 text-gray-500'
                          }`}>
                            {isCompleted ? '✓' : milestone}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-sm font-medium ${
                                isCompleted ? 'text-green-700' : isNext ? 'text-purple-700' : 'text-gray-600'
                              }`}>
                                {milestone} Referrals
                              </span>
                              <span className={`text-sm font-bold ${
                                isCompleted ? 'text-green-600' : isNext ? 'text-purple-600' : 'text-gray-500'
                              }`}>
                                {isCompleted ? '✓ Earned' : isNext ? 'Next Goal' : '25 Credits'}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full transition-all ${
                                  isCompleted ? 'bg-green-500' : isNext ? 'bg-purple-500' : 'bg-gray-300'
                                }`}
                                style={{ 
                                  width: isCompleted 
                                    ? '100%' 
                                    : isNext 
                                      ? `${((referralStats?.progress_to_next_bonus || 0) / (milestone - ((referralStats?.bonuses_earned || 0) * 5))) * 100}%`
                                      : '0%'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-6 p-4 bg-purple-100 rounded-lg border border-purple-200">
                    <p className="text-sm text-purple-900 font-medium">
                      💡 <strong>How it works:</strong> For every 5 friends who sign up using your referral code, you earn 25 bonus credits automatically!
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Credit History */}
              <Card>
                <CardHeader>
                  <CardTitle>Credit History</CardTitle>
                </CardHeader>
                <CardContent>
                  {creditHistory.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No transactions yet</p>
                  ) : (
                    <div className="space-y-2">
                      {creditHistory.map((transaction, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              transaction.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                            }`}>
                              {transaction.type === 'credit' ? (
                                <TrendingUp className="w-5 h-5 text-green-600" />
                              ) : (
                                <TrendingUp className="w-5 h-5 text-red-600 rotate-180" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium">{transaction.reason}</p>
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(transaction.timestamp)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-lg font-bold ${
                              transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {transaction.type === 'credit' ? '+' : '-'}{transaction.amount}
                            </p>
                            <p className="text-xs text-gray-500">Balance: {transaction.balance_after}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Referred Users */}
              {referralStats?.referred_users && referralStats.referred_users.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>People You've Referred ({referralStats.total_referrals})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {referralStats.referred_users.map((user, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div>
                            <p className="font-medium">{user.full_name}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                          <p className="text-xs text-gray-400">
                            {formatDate(user.created_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
